import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'common-toggle-panel',
  templateUrl: './toggle-panel.component.html',
  styleUrls: ['./toggle-panel.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TogglePanelComponent),
      multi: true,
    },
  ],
})
export class TogglePanelComponent implements ControlValueAccessor, OnChanges {
  /** Optional label shown at left side (Figma: toggle panel label). When provided, it overrides the yes/no state label. */
  @Input() label?: string;
  /** Optional subtitle shown below the label (same pattern as `common-panel`). */
  @Input() subtitle?: string;
  @Input() activeLabel: string = 'Sí';
  @Input() inactiveLabel: string = 'No';
  @Input() checked: boolean = false;
  @Input() disabled: boolean = false;
  /**
   * When `false`, no border; hover uses elevation shadow (same pattern as `common-panel`).
   * Default / omitted: bordered.
   */
  @Input() bordered?: boolean;

  /** Optional footer slot to render custom content (same pattern as `common-panel`). */
  @Input() footerTemplate?: TemplateRef<unknown>;
  /** Optional context for `footerTemplate` (available as `$implicit`). */
  @Input() footerContext?: unknown;
  /** Whether footer should be visible when `footerTemplate` is present. */
  @Input() showFooter: boolean = false;
  /** When true, keeps the footer rendered regardless of the toggle state/showFooter input. */
  @Input() alwaysExpanded: boolean = false;

  public panelClasses: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bordered']) {
      this.syncPanelClasses();
    }
  }

  constructor() {
    this.syncPanelClasses();
  }

  get isBorderless(): boolean {
    return this.bordered === false;
  }

  get shouldShowFooter(): boolean {
    return this.alwaysExpanded || this.showFooter;
  }

  @Output() change = new EventEmitter<boolean>();

  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  get currentLabel(): string {
    return this.checked ? this.activeLabel : this.inactiveLabel;
  }

  get displayLabel(): string {
    return this.label?.trim() ? this.label : this.currentLabel;
  }

  get hasSubtitle(): boolean {
    return !!this.subtitle?.trim();
  }

  onToggleChange(value: boolean): void {
    this.checked = value;
    this._onChange(value);
    this._onTouched();
    this.change.emit(value);
  }

  writeValue(value: boolean): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private syncPanelClasses(): void {
    this.panelClasses = this.isBorderless ? ['munily-toggle-panel--borderless'] : [];
  }
}
